/**
 * OutputEngine — audio playback via AudioWorklet + GainNode + <audio> element.
 *
 * Architecture (ElevenLabs-style):
 *   AudioWorklet (concat-processor) → GainNode → MediaStreamDestination → <audio>
 *
 * The <audio> element is the final output — browser-native, mobile-safe.
 * AudioContext is created once and never closed.
 * Interruption works by flushing the worklet buffer + fading the gain.
 */

const INTERRUPT_FADE_MS = 50;

export class OutputEngine {
  constructor() {
    this._ctx = null;
    this._worklet = null;
    this._gain = null;
    this._analyser = null;
    this._audioEl = null;
    this._volume = 1.0;
    this._playing = false;
    this._interruptTimer = null;
    this.onPlaybackStarted = null;
    this.onPlaybackEnded = null;
  }

  /**
   * Initialize the audio pipeline. Call inside a user gesture.
   * @param {number} sampleRate - Output sample rate (44100 for Cartesia, 48000 for Hume)
   */
  async init(sampleRate = 44100) {
    console.log('[OutputEngine] init, existing ctx:', !!this._ctx, 'state:', this._ctx?.state);
    if (this._ctx) {
      // Already initialized — just ensure it's running (iOS can suspend it)
      if (this._ctx.state === 'suspended') this._ctx.resume().catch(() => {});
      if (this._audioEl?.paused) this._audioEl.play().catch(() => {});
      return;
    }

    const AC = window.AudioContext || window.webkitAudioContext;
    this._ctx = new AC({ sampleRate });

    // Load the concat-processor worklet
    const workletUrl = new URL('./worklets/concat-processor.js', import.meta.url);
    await this._ctx.audioWorklet.addModule(workletUrl);

    // Build audio graph: worklet → gain → analyser → mediaStreamDest → <audio>
    this._worklet = new AudioWorkletNode(this._ctx, 'concat-processor');
    this._gain = this._ctx.createGain();
    this._gain.gain.value = this._volume;
    this._analyser = this._ctx.createAnalyser();

    const dest = this._ctx.createMediaStreamDestination();

    this._worklet.connect(this._gain);
    this._gain.connect(this._analyser);
    this._analyser.connect(dest);

    // <audio> element for output — mobile-safe, supports setSinkId
    this._audioEl = document.createElement('audio');
    this._audioEl.playsInline = true;
    this._audioEl.setAttribute('playsinline', '');
    this._audioEl.setAttribute('webkit-playsinline', '');
    this._audioEl.srcObject = dest.stream;
    this._audioEl.play().catch(() => {});

    // Listen for worklet playback state messages
    this._worklet.port.onmessage = (e) => {
      if (e.data.type === 'playbackStarted') {
        this._playing = true;
        this.onPlaybackStarted?.();
      } else if (e.data.type === 'playbackEnded') {
        this._playing = false;
        this.onPlaybackEnded?.();
      }
    };

    await this._ctx.resume();
  }

  /** Enqueue a Float32Array PCM chunk for playback. */
  play(float32) {
    if (!this._worklet) return;
    // Hook: forward a copy to Decart/Simli avatar if connected
    if (window.__decartAudioHook) {
      try { window.__decartAudioHook(new Float32Array(float32)); } catch (_) {}
    }
    // Pages that play audio elsewhere (e.g. Simli's video stream) can set
    // window.__muteLocalTTS = true to silence this engine without affecting
    // the PCM forwarded via the hook above.
    if (window.__muteLocalTTS) {
      float32 = new Float32Array(float32.length);
    }
    this._worklet.port.postMessage({ type: 'buffer', data: float32 }, [float32.buffer]);
  }

  /** Interrupt — flush all queued audio in the worklet. */
  interrupt() {
    if (!this._worklet) return;
    this._worklet.port.postMessage({ type: 'interrupt' });
  }

  /** Set output volume (0 to 1). */
  setVolume(v) {
    this._volume = Math.max(0, Math.min(1, v));
    if (this._gain) this._gain.gain.value = this._volume;
  }

  /** Whether audio is currently playing (worklet has data). */
  get playing() { return this._playing; }

  /** Get the AnalyserNode for visualizations. */
  get analyser() { return this._analyser; }

  /** Get the AudioContext (for sample rate info etc). */
  get context() { return this._ctx; }

  /**
   * Unlock audio on iOS — call inside a user gesture (click/tap).
   * Resumes AudioContext + plays silent audio to bless <audio> element.
   */
  unlock() {
    console.log('[OutputEngine] unlock, ctx state:', this._ctx?.state, 'audioEl paused:', this._audioEl?.paused);
    if (this._ctx?.state === 'suspended') this._ctx.resume().catch(() => {});
    if (this._audioEl) {
      this._audioEl.play().catch(() => {});
    }
  }

  /** Clean up everything. Call on unmount. */
  async close() {
    if (this._interruptTimer) clearTimeout(this._interruptTimer);
    if (this._audioEl) {
      this._audioEl.pause();
      this._audioEl.srcObject = null;
    }
    if (this._ctx) {
      await this._ctx.close().catch(() => {});
      this._ctx = null;
    }
    this._worklet = null;
    this._gain = null;
    this._analyser = null;
  }
}
